import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { Grid, Link, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import DocumentsScreen from './DocumentsScreen';

const menuSections = [
  {
    id: 'scopeAndApplication',
    text: '1. Scope and Application',
  },
  {
    id: 'definition',
    text: '2. “Personal Information” (PI)',
  },
  {
    id: 'whatPersonal',
    text: '3. What PI do we Collect?',
  },
  {
    id: 'howWeCollectInfo',
    text: '4. How we Collect PI',
  },
  {
    id: 'howWeUseInfo',
    text: '5. How we Use PI',
  },
  {
    id: 'howWeShareInfo',
    text: '6. How we Share PI',
  },
  {
    id: 'thirdParties',
    text: '7. Third Parties',
  },
  {
    id: 'howWeManageConsent',
    text: '8. How we Manage Consent',
  },
  {
    id: 'yourPrivacyChoices',
    text: '9. Your Privacy Choices',
  },
  {
    id: 'howWeStoreInfo',
    text: '10. How we Store and Safeguard PI',
  },
  {
    id: 'accessingAndCorrectingInfo',
    text: '11. Accessing Your PI',
  },
  {
    id: 'californiaConsumers',
    text: '12. California Consumers',
  },
  {
    id: 'children',
    text: '13. Children',
  },
  {
    id: 'changesToPrivacyPolicy',
    text: '14. Changes To Our Privacy Policy',
  },
  {
    id: 'whoCanYouContact',
    text: '15. Who Can You Contact?',
  },
];

const useStyles = makeStyles()((theme) => ({
  textDisabled: {
    fontWeight: 500,
  },
  textBold: {
    fontWeight: 600,
  },
  textSubtitle: {
    marginLeft: theme.spacing(3),
    fontWeight: 600,
  },
  textUnderline: {
    textDecoration: 'underline',
  },
  header: {
    paddingTop: 30,
  },
}));

function PrivacyPolicyScreen({ inModal, onNavigate }) {
  const { classes } = useStyles();

  const sections = menuSections.map((item) => ({
    id: item.id,
    text: item.text,
    subtext: item.subtext,
  }));

  const getTop = () => (
    <Grid container>
      <Grid item>
        <Typography paragraph color="primary" variant="subtitle2">
          Effective Date: October 29, 2020
        </Typography>
        <Typography className={classes.textDisabled} color="textSecondary" variant="body2" paragraph>
          Your privacy is important to JLIVE, Federation CJA and Jewish Federation of Metropolitan Detroit
          (collectively, <span className={classes.textBold}>“JLIVE” , “we”, “us” or “our” </span>). This Privacy Policy
          applies to our activities in Canada and the United States and describes our practices with respect to the
          collection, use and disclosure of personal information, as well as any rights you may have with respect to
          your personal information (as defined in the Definition of{' '}
          <Link href="#definition">“Personal Information”</Link> section).
        </Typography>
        <Typography className={classes.textDisabled} color="textSecondary" variant="body2" paragraph>
          If you are a California consumer, you may have additional rights under the California Consumer Privacy Act(
          <span className={classes.textBold}>“CCPA”</span>), as detailed in the{' '}
          <Link href="#californiaConsumers">California Consumers section</Link>.
        </Typography>
        <Typography className={classes.textDisabled} color="textSecondary" variant="body2">
          PLEASE READ THIS PRIVACY POLICY CAREFULLY. WHEN YOU USE JLIVE, WE MAY SHARE YOUR PERSONAL INFORMATION WITH
          ORGANIZATIONS THAT USE JLIVE AS A PLATFORM FOR THEIR EVENTS AND PROGRAMS. INFORMATION THAT JLIVE SHARES WITH
          SUCH ORGANIZATIONS IS NO LONGER IN THE CUSTODY OR UNDER THE CONTROL OF JLIVE. THOSE ORGANIZATIONS MAY USE THAT
          INFORMATION TO CONTACT YOU OR FOR OTHER PURPOSES IN ACCORDANCE WITH THEIR OWN PRIVACY POLICIES.
        </Typography>
      </Grid>
    </Grid>
  );

  return (
    <DocumentsScreen
      title="Privacy Policy"
      top={getTop()}
      sections={sections}
      inModal={inModal}
      onNavigate={onNavigate}
    >
      <Grid container>
        <Grid item xs={12} id="scopeAndApplication">
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography variant="h5" color="primary" className={classes.header}>
                1. Scope and Application
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body1">
                This Privacy Policy relates to the personal information collected by JLIVE, operating as a joint venture
                of Federation CJA and Jewish Federation of Metropolitan Detroit. This Privacy Policy applies whenever
                you interact with us, including when you visit our website or use our mobile applications on which this
                Privacy Policy is posted (collectively, the “Website”) or otherwise receive our services.
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} id="definition">
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography variant="h5" color="primary" className={classes.header}>
                2. Definition of “Personal Information”
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body1">
                “Personal information” is any information that identifies, or could be reasonably associated with, an
                individual and their household (collectively, <span className={classes.textBold}>“individual”</span> or
                <span className={classes.textBold}>“you”</span>). This may include, for example, your name, contact
                information, email-address, and information relating to your account with us (as the case may be). It
                may also include other types of more technical information, but only when this information can identify
                you as an individual. Information that is aggregated and/or de-identified and cannot be associated with
                an identifiable individual is not considered to be personal information.
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} id="whatPersonal">
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography variant="h5" color="primary" className={classes.header}>
                3. What Personal Information do we Collect?
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body1">
                We collect personal information in a variety of ways, including: directly from you, from third parties,
                and when you are interacting with us through our Website. We limit our collection of personal
                information to what is reasonably required to fulfill the purposes for which it was collected.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body1" paragraph>
                Personal information we collect may include:
              </Typography>
              <ul>
                <li>
                  <Typography variant="body1">
                    <span className={classes.textBold}>Identifiers and contact information</span>, such as your name,
                    email and postal addresses, telephone number, Internet Protocol (IP) address (if it can identify you
                    as an individual), photographs (if you upload them for your profile) or account information (for
                    example, if you have downloaded our app or you have created an account upon signing up for our
                    service);
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    <span className={classes.textBold}>Interests</span>,{' '}
                    <span className={classes.textBold}>such as performing arts, politics, or dating</span>, types of
                    activities, and the primary geographic area for which you are interested in discovering activities;
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    <span className={classes.textBold}>Demographic data</span>, such as whether you are over 13 years of
                    age, your lifestage, occupation, schooling, languages and your gender;
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    <span className={classes.textBold}>Transactional information</span>, such as donation or event
                    transactions and transaction history, as well as communications conducted with event planners or
                    organizations through our Website in relation to event matters such as reservations, refunds,
                    changes in schedule or venue;
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    <span className={classes.textBold}>Browsing activity</span> on our Website, such as pages and events
                    viewed, clickstream data, account login events, opt-out preferences, and IP addresses (as further
                    detailed in the <Link href="#howWeCollectInfo">How we Collect Personal Information </Link>section)
                    if it can identify you as an individual;
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    <span className={classes.textBold}>Connections</span>, such as organizations you choose to follow
                    and your organizational affiliations, your contacts within the Website and your contacts from social
                    media or other sources (where you have provided permission for us to view them);
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    <span className={classes.textBold}> Religious affiliation</span>, such as Conservadox, Conservative,
                    Culturally Jewish, Hasidic, Modern Orthodox, Orthodox (Baal Teshuva), Orthodox (Frum),
                    Reconstructionist, Reform, Traditional, or other;
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    <span className={classes.textBold}> Payment information</span>, such as credit or debit card number
                    when you make reservations or purchases;
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    <span className={classes.textBold}>Geolocation information</span> may be collected when you download
                    our app or use our Website, although this information may not identify you as an individual;
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    <span className={classes.textBold}>Any other information you provide </span>to us (as further
                    detailed in the <Link href="#howWeCollectInfo">How we Collect Personal Information</Link> section).
                  </Typography>
                </li>
              </ul>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} id="howWeCollectInfo">
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography variant="h5" color="primary" className={classes.header}>
                4. How we Collect Personal Information
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body1" paragraph>
                We collect personal information in a variety of ways, including: directly from you, from other sources,
                through online technologies (such as when you are interacting with us through our Website) and from
                third-party social networks.
              </Typography>
              <Typography variant="body1" paragraph>
                More specifically, we collect personal information in the following ways:
              </Typography>
              <Grid container>
                <Grid item>
                  <Typography variant="body1" paragraph className={classes.textSubtitle}>
                    a. Directly From You
                  </Typography>
                  <Typography variant="body1" paragraph>
                    You may provide personal information to us by mail, by email, over the telephone, through our
                    Website or in any other direct manner, for instance when you participate in a survey, make a request
                    to our customer service or upload content to our Website or social network pages (including photos
                    or videos).
                  </Typography>
                  <Typography variant="body1" paragraph className={classes.textUnderline}>
                    For example:
                  </Typography>
                  <Typography variant="body2" className={classes.textDisabled} color="textSecondary" paragraph>
                    When you voluntarily share information about yourself with us in order to join JLIVE, register for
                    an event through JLIVE, fill out optional information in your user profile, or respond to surveys or
                    emails, we will collect that information in order to provide you with our programs or services,
                    respond to your inquiry, improve our services, or for any other purpose disclosed to you in this
                    Privacy Policy or at the time of collection.
                  </Typography>
                </Grid>

                <Grid item>
                  <Typography variant="body1" paragraph className={classes.textSubtitle}>
                    b. From Organizations
                  </Typography>
                  <Typography variant="body1" paragraph>
                    We may receive personal information from other organizations, including but not limited to
                    organizations that use JLIVE to facilitate their programs and events. We may also receive your
                    personal information from other sources with your consent or when authorized by law.
                  </Typography>
                  <Typography variant="body1" paragraph className={classes.textUnderline}>
                    For example:
                  </Typography>
                  <Typography variant="body2" className={classes.textDisabled} color="textSecondary" paragraph>
                    We may ask for your permission for an organization to release your personal information and share it
                    with JLIVE, and we may share this permission with said organization when we facilitate a program or
                    event held by that organization and made available through JLIVE, or when we partner with other
                    organizations to provide certain programs or events.
                  </Typography>
                </Grid>

                <Grid item>
                  <Typography variant="body1" paragraph className={classes.textSubtitle}>
                    c. Through Online Technologies
                  </Typography>
                  <Typography variant="body1" paragraph>
                    We may collect certain types of information electronically when you interact with our Website, email
                    or social media accounts, online advertising, or through the use of our or a third party’s
                    technologies, which include cookies, web beacons or single pixel gifs or analytics engines. This
                    information supports the functioning of our Website, and helps us understand what actions you take
                    on our Website and whether our website is working properly.
                  </Typography>
                  <Typography variant="body1" paragraph>
                    We may combine this information with other information such as your transaction history. We do this
                    to support our customer understanding and to provide you with a more tailored user experience,
                    including event recommendations and advertising. This includes serving interest-based advertising to
                    you, subject to your right to withdraw consent. To learn more about the privacy choices available to
                    you, please see <Link href="#yourPrivacyChoices">Your Privacy Choices</Link>.
                  </Typography>
                  <Typography variant="body1" paragraph className={classes.textUnderline}>
                    For example:
                  </Typography>
                  <ul>
                    <li>
                      We may collect information about your device and your browser, clickstream data, event data, crash
                      data, and other technical data when you visit our Website.
                    </li>
                    <li>We may use your location to auto-select features when you visit our Website.</li>
                    <li>
                      We may use your personal transaction information to provide you with interest-based advertisements
                      on events you participate in frequently, or products or services related to such events or other
                      interests.
                    </li>
                    <li>
                      We may collect, combine and categorize your personal information on an aggregated basis to
                      identify transaction and other usage patterns to help us analyze the use of our services to better
                      serve users.
                    </li>
                  </ul>
                  <Typography variant="body1" paragraph>
                    The technologies we use include:
                  </Typography>
                  <ul>
                    <li>
                      Cookies, which are small text files that are saved on your computer when you visit a website so
                      that information can be saved between visits, such as your login credentials or language
                      preferences. For example, cookies allow you to log in quickly when you visit our Website.
                    </li>
                    <li>
                      Web beacons, and single pixel gifs, which are small image files that have information about you,
                      such as your IP address, that can be downloaded when you visit a website or open an e-mail. This
                      allows us to understand your online behaviour, monitor our email delivery, and provide you with
                      interest-based advertising. These tools also allow our third-party tracking tools to gather
                      information, such as your IP address, and provide this back to us in an anonymized, aggregate form
                      (i.e. in a manner that prevents us from identifying you personally). Aggregate information refers
                      to personal information compiled and expressed in a summary form where no personal identifiers are
                      included.
                    </li>
                    <li>
                      Other technologies, such as analytics engines, which pull usage data from multiple sources and
                      help manage and collect this data to use for personalization, interest-based advertising,
                      customizing content and other methods to gain insights into our customers’ needs and preferences.
                    </li>
                    <li>
                      With respect to our mobile application, we use Identifier for Advertising for iOS and Google
                      Advertising ID for Android.
                    </li>
                  </ul>
                  <Typography variant="body1" paragraph>
                    We may use Google Analytics and Firebase analytics services provided by Google Inc. Google Analytics
                    uses cookies to analyze your use of our Website, to create reports about visitor activities for us
                    and to provide further services associated with the use of the Website and the Internet, and
                    Firebase provides analytics for mobile apps. We may also use Amazon Redshift, an analytics platform
                    provided by Amazon Web Services, Inc., to analyze combinations of data collected in relation to
                    events, marketing, views and clickthroughs. We may also use other third party tools, such as
                    Fullstory, that permit us to replay user sessions for analysis of user behaviour and Website
                    performance.
                  </Typography>
                  <Typography variant="body1" paragraph>
                    You may delete or disable certain of these technologies at any time via your browser or through your
                    mobile device or app settings. However, if you do so, you may not be able to use some of the
                    features on our Website. To learn more about the privacy choices available to you, please see{' '}
                    <Link href="#yourPrivacyChoices">Your Privacy Choices</Link>.
                  </Typography>
                </Grid>

                <Grid item>
                  <Typography variant="body1" paragraph className={classes.textSubtitle}>
                    d. Third Party Social Networks
                  </Typography>
                  <Typography variant="body1" paragraph>
                    Third-party social networks that provide interactive plug-ins to enable social network features
                    (e.g., to connect to Facebook) on the Website may use cookies to gather information on your use of
                    the Website. How this collected data is used by a third party is dependent on the privacy policy of
                    the social network which is available on the appropriate parties’ website. JLIVE encourages you to
                    review the privacy policies of such third parties as well.
                  </Typography>
                  <Typography variant="body1" paragraph>
                    We may also obtain and use analytical and statistical information from third-party social networks
                    to help us measure performance and effectiveness of content we display on social networks, for
                    example, by measuring impressions and clicks on the content JLIVE promotes, or tracking what events
                    you share through the social network, who you share them with, and the outcomes of those sharing
                    events, such as whether it led someone to register for an event.
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} id="howWeUseInfo">
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography variant="h5" color="primary" className={classes.header}>
                5. How we Use Personal Information
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1" paragraph>
                We may use all categories of personal information detailed in section 3 to provide you with services, to
                manage our operations, to communicate offers and information we think might interest you, to generally
                enhance your experience with us, and as otherwise permitted or required by law.
              </Typography>
              <Typography variant="body1" paragraph>
                Learn more about how we use your personal information for: providing services, managing our operations,
                advertising, and conducting research and data analytics.
              </Typography>
              <Typography variant="body1" paragraph>
                We use your personal information for the following purposes:
              </Typography>
              <Grid container>
                <Grid item>
                  <Typography variant="body1" paragraph className={classes.textSubtitle}>
                    a. Providing Services
                  </Typography>
                  <Typography variant="body1" paragraph>
                    We use your personal information in order to provide programs, events and services, for instance:
                  </Typography>
                  <ul>
                    <li>Registering you for an event or service;</li>
                    <li>Verifying your identity; </li>
                    <li>Processing your transactions and refunds, where applicable; </li>
                    <li>Responding to your inquiries; </li>
                    <li>Taking or verifying instructions from you; </li>
                    <li>Creating, administering, and maintaining your accounts;</li>
                    <li>
                      With your consent, to verify your credit when such verification is necessary for certain of our
                      services;
                    </li>
                    <li>Fulfilling your requests, inquiries, and purchases.</li>
                  </ul>
                </Grid>
                <Grid item>
                  <Typography variant="body1" paragraph className={classes.textSubtitle}>
                    b. Managing Our Operations
                  </Typography>
                  <Typography variant="body1" paragraph>
                    We use your personal information to manage our operations, for instance:
                  </Typography>
                  <ul>
                    <li>
                      Managing and facilitating the use of our Website, which may include using cookies and other
                      similar technology (as further detailed in the{' '}
                      <Link href="#howWeCollectInfo">How we Collect Personal Information</Link> section);
                    </li>
                    <li>Enabling your participation in programs, events, contests, promotions, or surveys;</li>
                    <li>Maintaining the security of the public, our users and our employees; </li>
                    <li>Protecting ourselves and third parties from errors and fraud;</li>
                    <li>Monitoring and investigating incidents and managing claims;</li>
                    <li>Maintaining our services; and </li>
                    <li>Meeting our contractual, legal and regulatory obligations.</li>
                  </ul>
                </Grid>
                <Grid item>
                  <Typography variant="body1" paragraph className={classes.textSubtitle}>
                    c. Advertising
                  </Typography>
                  <Typography variant="body1" paragraph>
                    We use your personal information to inform you about programs, events, services, promotions,
                    contests, or surveys, both on our own behalf and those of third parties, that we believe may be of
                    interest to you. We may do this in a variety of ways:
                  </Typography>
                  <ul>
                    <li>By communicating with you by email, telephone, SMS, direct mail;</li>
                    <li>By delivering online advertisements relevant for you; and</li>
                    <li>By customizing the content you see on our Website.</li>
                  </ul>
                  <Typography variant="body1" paragraph>
                    If you no longer wish to receive commercial electronic messages, please let us know by following the
                    unsubscribe directions provided in every commercial electronic message. For more information on
                    managing your marketing preferences, please see{' '}
                    <Link href="#yourPrivacyChoices">Your Privacy Choices</Link>.
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1" paragraph className={classes.textSubtitle}>
                    d. Conducting Research and Data Analytics
                  </Typography>
                  <Typography variant="body1" paragraph>
                    We may use your personal information in order to conduct research and data analytics by tracking and
                    analyzing current or previously collected information for the following purposes:
                  </Typography>
                  <ul>
                    <li>Improving or developing new services; </li>
                    <li>Managing and developing our operations;</li>
                    <li>
                      Understanding user needs, preferences and behaviour, and customizing how we tailor services to our
                      users based on their interests;
                    </li>
                    <li>Measuring the effectiveness of our marketing; and</li>
                    <li>Understanding how visitors interact with our Website and ensuring it works correctly.</li>
                  </ul>
                  <Typography variant="body1" paragraph>
                    When possible, we will use your information in an aggregated and/or de-identified format.
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} id="howWeShareInfo">
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography variant="h5" color="primary" className={classes.header}>
                6. How we Share Personal Information
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1" paragraph>
                We may share your personal information with Federation CJA and Jewish Federation of Metropolitan
                Detroit, third party organizations that have joined JLIVE in order to provide a platform for their
                events and programs, and service providers (companies operating on our behalf) for the purposes
                described in this Privacy Policy and in accordance with applicable law. We do not sell your personal
                information to any organization or person; the only exception to this would be if we sell or transfer
                any part of our business. We will not share your personal information with any non affiliated third
                party without your prior consent, other than as provided in this Privacy Policy or as otherwise
                authorized by applicable law.
              </Typography>
              <Typography variant="body1" paragraph>
                Learn more about how we may share personal information: with Federation CJA and Jewish Federation of
                Metropolitan Detroit, with third party organizations, with third partyservice providers, in the course
                of asale or transfer of business or other transaction, for other permitted reasons, and with your
                consent.
              </Typography>
              <Grid container>
                <Grid item>
                  <Typography variant="body1" paragraph className={classes.textSubtitle}>
                    a. Federation CJA and Jewish Federation of Metropolitan Detroit
                  </Typography>
                  <Typography variant="body1" paragraph>
                    We may share your personal information with Federation CJA and/or Jewish Federation of Metropolitan
                    Detroit where they make use of JLIVE as organizers, i.e. as a platform for their own programs or
                    events, or those that they are participating in as co-organizers, sponsors, or otherwise. Although
                    we only share the personal information needed by Federation CJA and/or Jewish Federation of
                    Metropolitan Detroit to provide you with access to the relevant events or programs and communicate
                    with you regarding such events or programs, as well as any optional information you choose to
                    provide, once JLIVE shares such information with Federation CJA and/or Jewish Federation of
                    Metropolitan Detroit, it is no longer in the custody or under the control of JLIVE. Federation CJA
                    and/or Jewish Federation of Metropolitan Detroit, as applicable, may use that information to contact
                    you or for other purposes in accordance with their own privacy policies. JLIVE encourages you to
                    review the privacy policies of the relevant organization before registering for its events or
                    programs.
                  </Typography>
                  <Typography variant="body1" paragraph>
                    We may also share information with Federation CJA and Jewish Federation of Metropolitan Detroit in
                    aggregated and/or de-identified format in order to permit each of them to conduct research and data
                    analytics.
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1" paragraph className={classes.textSubtitle}>
                    b. Organizations
                  </Typography>
                  <Typography variant="body1" paragraph>
                    We may share your personal information with third party organizations that use JLIVE as a platform
                    for their events and programs. Although we only share the personal information needed by such
                    organizations to provide you with access to the relevant events or programs and communicate with you
                    regarding such events or programs, as well as any optional information you choose to provide, once
                    JLIVE shares such information with such organizations, it is no longer in the custody or under the
                    control of JLIVE. Those organizations may use that information to contact you or for other purposes
                    in accordance with their own privacy policies. JLIVE encourages you to review the privacy policies
                    of the relevant organization before registering for its events or programs.
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1" paragraph className={classes.textSubtitle}>
                    c. Service Providers
                  </Typography>
                  <Typography variant="body1" paragraph>
                    In the course of providing our services we may share all categories of personal information detailed
                    in the section <Link href="#whatPersonal">What Personal Information do we Collect?</Link> with third
                    party service providers who perform services on our behalf. These service providers help us operate
                    our business, technology systems and applications, internal procedures, infrastructure and
                    advertising and marketing. They provide services to us, such as fulfilling purchases, processing
                    credit card payments, performing credit checks, sending emails and postal mail, call centres, data
                    hosting, contest administration, providing advertisements, and analytics services (e.g. tracking
                    effectiveness of our campaigns and analyzing usage of our Website). We require these service
                    providers to limit their access to and/or use of personal information to what is required to provide
                    their services and require that those third parties adhere to confidentiality as well as security
                    procedures and protections.
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1" paragraph className={classes.textSubtitle}>
                    d. Sale or Transfer of Business or Other Transaction
                  </Typography>
                  <Typography variant="body1" paragraph>
                    We may disclose personal information to a third party in connection with a sale or transfer of
                    business or assets, an amalgamation, reorganization or financing of parts of our business (including
                    the proceedings of insolvency or bankruptcy). In the event the transaction is completed, your
                    personal information will remain protected by applicable privacy laws. In the event the transaction
                    is not completed, we will require the other party not to use or disclose your personal information
                    in any manner whatsoever and to completely delete such information, in compliance with applicable
                    laws.
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1" paragraph className={classes.textSubtitle}>
                    e. Other Permitted Reasons
                  </Typography>
                  <Typography variant="body1" paragraph>
                    Applicable laws may permit or require the use, sharing, or disclosure of personal information
                    without consent in specific circumstances (e.g., when investigating and preventing suspected or
                    actual illegal activities, including fraud, or to assist government and law enforcement agencies).
                    These circumstances include situations when permitted or required by law or when necessary to
                    protect JLIVE, our employees, our customers, or others. If this happens, we will not share more
                    personal information than is reasonably required to fulfill that particular purpose.
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1" paragraph className={classes.textSubtitle}>
                    f. With Your Consent
                  </Typography>
                  <Typography variant="body1" paragraph>
                    Other than the purposes listed above, we may, with your implied or express consent, share or
                    disclose your personal information outside of JLIVE, in accordance with applicable laws.
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} id="thirdParties">
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography variant="h5" color="primary" className={classes.header}>
                7. Third Parties
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1" paragraph>
                Third parties, such as advertising partners, may use tracking methods such as but not limited to cookies
                to achieve their own business goals and purposes by relating and combining information about your usage
                of our Website to any other personal information they may have collected on you. We do not collect, use
                or disclose such information, which is subject to the privacy policies of the third party organization.
                JLIVE encourages you to review the privacy policies of such organizations as well.
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} id="howWeManageConsent">
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography variant="h5" color="primary" className={classes.header}>
                8. How we Manage Consent
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1" paragraph>
                By joining JLIVE or registering for an event or program through JLIVE, responding to surveys or emails
                or otherwise submitting information to us in connection with using our services, you are providing your
                consent to the collection, use and disclosure of personal information as set out in this Privacy Policy.
                In some cases, your consent may be “implied” i.e. your permission is assumed based on your action or
                inaction at the point of collection, use or sharing of your personal information.
              </Typography>
              <Typography variant="body1" paragraph>
                We will generally obtain consent when we want to use personal information for a new purpose or for a
                purpose other than those stated at the time of collection in this Privacy Policy or in the terms and
                conditions of a specific program, event, service, contest, or promotion you signed up for, participated
                in or purchased. Your consent can be withdrawn at any time, except in limited circumstances, including
                legal or regulatory requirements or as a result of your contractual obligations with us. For information
                on how to manage your privacy preferences, please see{' '}
                <Link href="#yourPrivacyChoices">Your Privacy Choices.</Link>
              </Typography>
              <Typography variant="body1" paragraph>
                If you choose not to provide us with certain personal information or if you withdraw your consent, where
                such withdrawal is available, we may not be able to offer you the programs, events, services, contests,
                promotions or information that you requested or that could be offered to you.
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} id="yourPrivacyChoices">
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography variant="h5" color="primary" className={classes.header}>
                9. Your Privacy Choices
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1" paragraph>
                We want you to clearly understand your choices and make informed decisions about your privacy options.
                There are several options available for you to manage your privacy preferences, including, for example:
                managing your preferences within your account when applicable, contacting us directly, changing your
                browser settings on your device, and/or, where available, using third party unsubscribe functionality.
              </Typography>
              <Typography variant="body1" paragraph>
                Learn more about how to change your privacy choices through your account, when you register or by
                contacting us directly, and using third party unsubscribe functionality.
              </Typography>
              <Grid container>
                <Grid item>
                  <Typography variant="body1" paragraph className={classes.textSubtitle}>
                    a. Within your Account
                  </Typography>
                  <Typography variant="body1" paragraph>
                    If you have a JLIVE account, you can update your preferences within your account. Please see our
                    help pages for more information.
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1" paragraph className={classes.textSubtitle}>
                    b. When You Register or by Contacting Us Directly
                  </Typography>
                  <Typography variant="body1" paragraph>
                    You may opt in to receiving email or SMS communications at the time that you join JLIVE or register
                    for an event or program through JLIVE, enter our contests, promotions or surveys, or any time
                    thereafter through your online account. We may also have your implied consent to send you such
                    marketing electronic communications under applicable laws.
                  </Typography>
                  <Typography variant="body1" paragraph>
                    You may opt out of:
                  </Typography>
                  <ul>
                    <li>
                      Receiving email or SMS marketing communications, by clicking “unsubscribe” within any marketing
                      email you receive, texting “STOP” within any SMS marketing you receive, or through preferences in
                      your account (if you have created one);
                    </li>
                    <li>
                      Receiving interest-based advertisements by changing your browser settings on your device, and/or
                      using third party unsubscribe functionality (e.g., via Google or Facebook) to change your
                      preferences within their platforms. Please note that disabling, blocking or deleting some of these
                      technologies, such as cookies, may cause you to lose the functionality of certain features on our
                      Website.
                    </li>
                  </ul>
                  <Typography variant="body1" paragraph>
                    Please note the following regarding withdrawal of consent or changes in your privacy preferences:
                  </Typography>
                  <ul>
                    <li>
                      Even if you have opted out of receiving marketing communications from us, we may still contact you
                      for transactional purposes, in compliance with applicable laws (e.g., for customer service,
                      program or event information, or service or reminder notices). We may also need to contact you
                      with questions or information regarding your customer service inquiries; and
                    </li>
                    <li>
                      It may take some time for all of our records to reflect changes in your preferences (e.g., if you
                      request that you not receive personalized marketing communications from JLIVE, your preference may
                      not be captured for a promotion already in progress).
                    </li>
                  </ul>
                </Grid>
                <Grid item>
                  <Typography variant="body1" paragraph className={classes.textSubtitle}>
                    c. Using Third Party Functionality
                  </Typography>
                  <Typography variant="body1" paragraph>
                    We may use third parties (e.g., web companies or social media platforms) that use certain
                    technologies to analyze your browsing behaviour as you visit our Website. You can manage your
                    privacy preferences with respect to certain third parties, such as Google, by using the unsubscribe
                    functionality within their platforms. Please note that disabling, blocking or deleting some of these
                    technologies, such as cookies, may cause you to lose the functionality of certain features on our
                    Website.
                  </Typography>
                  <Typography variant="body1" paragraph>
                    For example, Google Analytics uses cookies to analyze your browsing behaviour on our Website. This
                    information is not provided to us in a personally identifiable format and is collected through your
                    ad settings on your device or browser. If you would like to opt out of Google’s ad settings, please
                    go to www.google.com/settings/ads or use the Google opt out browser add-on located at
                    https://tools.google.com/dlpage/gaoptout.
                  </Typography>
                  <Typography variant="body1" paragraph>
                    We also may embed links to other websites, including social media websites, on our Website. For more
                    information about social media plug-in protocols, such as “Like” buttons on Twitter, Facebook or
                    Instagram and their impact on your privacy rights, please visit the appropriate social media help
                    centre (e.g., Facebook’s Help Center at https://www.facebook.com/help/).
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} id="howWeStoreInfo">
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography variant="h5" color="primary" className={classes.header}>
                10. How we Store and Safeguard Personal Information
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1" paragraph>
                We take the security of your personal information very seriously and are committed to protecting your
                privacy by using a combination of administrative, physical, and technical safeguards. Your personal
                information may be stored in foreign jurisdictions, in which case it will be subject to foreign laws. We
                store your personal information for as long as it is necessary to provide you with our services and for
                a reasonable time thereafter, or as permitted or required by law.
              </Typography>
              <Typography variant="body1" paragraph>
                Learn more about <Link href="#howWeStoreInfo">how we protect personal information</Link>, where we keep
                personal information and cross-border transfers and how long we keep personal information.
              </Typography>
              <Grid container>
                <Grid item>
                  <Typography variant="body1" paragraph className={classes.textSubtitle}>
                    a. How we Protect Personal Information
                  </Typography>
                  <Typography variant="body1" paragraph>
                    We employ organizational, physical and technological measures to protect the confidentiality of
                    personal information and to safeguard personal information against loss or theft, as well as
                    unauthorized access, disclosure, copying, use or modification, in light of, among other things, the
                    sensitivity of the information and the purposes for which it is to be used. These safeguards also
                    apply when we dispose of or destroy your personal information.
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1" paragraph className={classes.textSubtitle}>
                    b. Where we Keep Personal Information and Cross-border Transfers
                  </Typography>
                  <Typography variant="body1" paragraph>
                    We may store personal information within Canada or the United States. Some of our service providers
                    may also access, process or store your personal information outside of the country where you reside.
                    We use reasonable safeguards to ensure that our service providers protect your personal information
                    wherever it is used or stored.
                  </Typography>
                  <Typography variant="body1" paragraph>
                    As a result, when your personal information is used or stored in a jurisdiction other than where you
                    are residing, it may be subject to the law of this foreign jurisdiction, including any law
                    permitting or requiring disclosure of the information to the government, government agencies, courts
                    and law enforcement in that jurisdiction.
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1" paragraph className={classes.textSubtitle}>
                    c. How Long we Keep Personal Information
                  </Typography>
                  <Typography variant="body1" paragraph>
                    We will store your personal information for as long as necessary to fulfill the purposes for which
                    it was collected, except where otherwise required or permitted by law. Once no longer required, your
                    personal information will be securely destroyed or anonymized (so the information no longer
                    identifies you).
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} id="accessingAndCorrectingInfo">
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography variant="h5" color="primary" className={classes.header}>
                11. Accessing and Correcting Your Personal Information
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1" paragraph>
                You may have the right to access and rectify the personal information we hold about you under the law
                applicable in the jurisdiction where you reside. In this case, upon request, we will provide you with
                access to your personal information within a reasonable timeframe, in compliance with applicable laws.
                It is your responsibility to provide accurate, correct and complete information.
              </Typography>
              <Typography variant="body1" paragraph>
                You can request access or rectification by contacting the applicable Privacy Office as described in the
                “<Link href="#whoCanYouContact">Who Can You Contact With Privacy Questions?</Link>” section. If you
                reside in California, please refer to the <Link href="#californiaConsumers">California Consumers </Link>
                section for more information on how to exercise your rights
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} id="californiaConsumers">
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography variant="h5" color="primary" className={classes.header}>
                12. California Consumers
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1" paragraph>
                This section describes the rights you have under the CCPA if you are a consumer residing in California.
              </Typography>
              <Typography variant="body1" paragraph>
                The categories of personal information we collect about California consumers (and that we may have
                collected in the last twelve months) and the purposes for which we collect this personal information are
                listed, respectively, in sections{' '}
                <Link href="#whatPersonal">What Personal Information do we Collect?</Link>,{' '}
                <Link href="#howWeCollectInfo">How we Collect Personal Information</Link> and{' '}
                <Link href="#howWeUseInfo">How we Use Personal Information</Link> of this Privacy Policy. We do not sell
                your personal information. We may disclose all personal information we collect for business purposes to
                service providers (and may have done so in the last twelve months), as further detailed in the{' '}
                <Link href="#howWeShareInfo">How we Share Personal Information</Link> section of this Privacy Policy.
              </Typography>
              <Grid container>
                <Grid item>
                  <Typography variant="body1" paragraph className={classes.textSubtitle}>
                    d. Request to Know about the Personal Information we have Collected
                  </Typography>
                  <Typography variant="body1" paragraph>
                    Once every 12 months, you may submit a verifiable request that we disclose the following information
                    to you:
                  </Typography>
                  <ul>
                    <li>
                      The categories and specific pieces of personal information that we have collected about you in the
                      last 12 months, as well as the sources from which personal informal is collected, the business and
                      commercial purposes for collecting it, the categories of third parties with whom we have shared
                      it; and
                    </li>
                    <li>
                      The categories of personal information about you that we have disclosed for a business purpose in
                      the last 12 months.
                    </li>
                  </ul>
                </Grid>

                <Grid item>
                  <Typography variant="body1" paragraph className={classes.textSubtitle}>
                    e. Right to Request that we Delete your Personal Information
                  </Typography>
                  <Typography variant="body1" paragraph>
                    You may submit a verifiable request that we delete any personal information that we have collected
                    about you, subject to the exceptions provided for by the CCPA.
                  </Typography>
                </Grid>

                <Grid item>
                  <Typography variant="body1" paragraph className={classes.textSubtitle}>
                    f. Methods for Exercising your Rights
                  </Typography>
                  <Typography variant="body1" paragraph>
                    You may exercise these rights using one of the methods described in{' '}
                    <Link href="#whoCanYouContact">section 15</Link>.
                  </Typography>
                </Grid>

                <Grid item>
                  <Typography variant="body1" paragraph className={classes.textSubtitle}>
                    g. Non-Discrimination Right
                  </Typography>
                  <Typography variant="body1" paragraph>
                    We will not, because you have exercised any of the rights detailed in this section 12, deny you any
                    goods or services, charge different prices or rates for goods or services, or provide you with a
                    different level or quality of goods or services.
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} id="children">
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography variant="h5" color="primary" className={classes.header}>
                13. Children
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1" paragraph>
                We do not knowingly request or collect personal information from children under 13 years of age without
                prior verifiable consent of his or her parent or legal guardian and complying with any other legal
                requirements. If we become aware that we have unknowingly collected personal information about a child
                without verifiable parental or legal guardian consent, we will delete this information from our records
                or take reasonable available steps to de-identify the information.
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} id="changesToPrivacyPolicy">
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography variant="h5" color="primary" className={classes.header}>
                14. Changes To Our Privacy Policy
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1" paragraph>
                We may make changes to this Privacy Policy from time to time. Any changes we make will become effective
                when we post a modified version of the policy on our Website. If the changes we make are significant, we
                will provide a more prominent notice when required by applicable laws. By continuing to use our services
                after the modified version of the Privacy Policy has been posted or you have been informed of such
                update, you are accepting the changes to the Privacy Policy. If you do not agree to the changes in our
                Privacy Policy, it is your responsibility to stop using our services. It is your obligation to ensure
                that you read, understand and agree to the latest version of the Privacy Policy. The “Effective Date” at
                the top of the Privacy Policy indicates when it was last updated.
              </Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} id="whoCanYouContact">
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography variant="h5" color="primary" className={classes.header}>
                15. Who Can You Contact With Privacy Questions?
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1" paragraph>
                If you have any questions about how we handle your personal information, please contact us as indicated
                below. If you are participating in a program, event, promotion, contest or survey that is offered by us
                jointly with a third party, the third party may hold your personal information. If you have any
                questions or concerns, we will direct you to the appropriate party so that you may make enquiries as to
                that party’s privacy policies and practices.
              </Typography>
              <Typography variant="body1" paragraph className={classes.textBold}>
                If you are located in Canada:
              </Typography>
              <Typography variant="body1" paragraph>
                <Link href="mailto:privacy@federationcja.org" color="textPrimary">
                  privacy@federationcja.org
                </Link>
              </Typography>

              <Typography variant="body1" paragraph>
                Tel: 514-343-8708
              </Typography>
              <Typography variant="body1" paragraph>
                Privacy Office, Federation CJA, 1 Cummings Square, Montreal, Quebec H3W 1M6 Canada
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1" paragraph className={classes.textBold}>
                If you are located in the United States:
              </Typography>
              <Typography variant="body1" paragraph>
                Tel: 248-642-4260
              </Typography>
              <Typography variant="body1" paragraph>
                Privacy Office, Jewish Federation of Metropolitan Detroit, 6735 Telegraph Rd #30, Bloomfield Hills, MI
                48301
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </DocumentsScreen>
  );
}

PrivacyPolicyScreen.propTypes = {
  inModal: PropTypes.bool,
  onNavigate: PropTypes.func,
};

PrivacyPolicyScreen.defaultProps = {
  inModal: false,
  onNavigate: undefined,
};

export default memo(PrivacyPolicyScreen);
